import { DASHBOARD_URL } from '../App';

const connectors = [
  {
    slug: 'gunbroker',
    name: 'GunBroker',
    description: 'The leading online firearms marketplace. Sync your inventory, manage listings, and import orders seamlessly.',
    status: 'live' as const,
    features: [
      'Push listings from Odoo inventory',
      'Real-time quantity and pricing sync',
      'Automatic order import',
      'Category and shipping mapping',
      'Multi-account support',
    ],
  },
  {
    slug: 'ebay',
    name: 'eBay',
    description: 'Reach millions of buyers worldwide. Create and manage listings, sync inventory, and automatically import orders.',
    status: 'live' as const,
    features: [
      'Create and publish listings',
      'Quantity synchronization',
      'Order import to Odoo',
      'Pricing rules and updates',
      'Multi-store management',
    ],
  },
  {
    slug: 'shipstation',
    name: 'ShipStation',
    description: 'Streamline your shipping workflow. Push orders for fulfillment and sync tracking information back to Odoo.',
    status: 'coming-soon' as const,
    features: [
      'Push orders for fulfillment',
      'Sync tracking numbers',
      'Rate shopping integration',
      'Automated status updates',
      'Multi-carrier support',
    ],
  },
  {
    slug: 'amazon',
    name: 'Amazon',
    description: "Connect to the world's largest marketplace. Manage listings, inventory, and orders from Odoo.",
    status: 'coming-soon' as const,
    features: [
      'FBA and FBM support',
      'Listing management',
      'Inventory synchronization',
      'Order import',
      'Multi-marketplace support',
    ],
  },
];

export default function ConnectorsPage() {
  return (
    <div>
      {/* Hero - extends to top, nav floats over */}
      <section className="pt-32 pb-20 sm:pt-40 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-blue-400 font-semibold mb-4">Marketplace Connectors</p>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Connect your Odoo to leading marketplaces
            </h1>
            <p className="text-xl text-white/70 leading-relaxed">
              Sync inventory, manage listings, and pull orders from e-commerce platforms directly into your ERP. No manual data entry, no sync errors.
            </p>
          </div>
        </div>
      </section>

      {/* Connectors Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {connectors.map((connector) => (
              <div
                key={connector.slug}
                className="bg-white rounded-xl border border-gray-200 p-8 hover:border-gray-300 hover:shadow-lg transition-all"
              >
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-slate-900">{connector.name}</h2>
                  {connector.status === 'live' ? (
                    <span className="text-xs font-medium text-green-600 bg-green-50 px-3 py-1 rounded-full">
                      Live
                    </span>
                  ) : (
                    <span className="text-xs font-medium text-amber-600 bg-amber-50 px-3 py-1 rounded-full">
                      Coming Soon
                    </span>
                  )}
                </div>

                <p className="text-gray-600 mb-6">{connector.description}</p>

                <ul className="space-y-3 mb-8">
                  {connector.features.map((feature, i) => (
                    <li key={i} className="flex items-start text-sm text-gray-600">
                      <svg
                        className="w-4 h-4 text-slate-400 mr-3 mt-0.5 flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>

                {connector.status === 'live' ? (
                  <a
                    href={`${DASHBOARD_URL}/signup`}
                    className="inline-flex items-center text-blue-600 font-semibold hover:underline"
                  >
                    Get Started →
                  </a>
                ) : (
                  <span className="text-gray-400 text-sm">Available soon</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Request Section */}
      <section className="py-20 bg-gray-50 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">
            Need a different connector?
          </h2>
          <p className="text-gray-600 mb-8 max-w-xl mx-auto">
            We're actively building new integrations. Let us know what marketplace or service you need connected to Odoo.
          </p>
          <a
            href="mailto:support@nexuserp.app"
            className="inline-flex items-center justify-center bg-slate-900 hover:bg-slate-800 text-white px-6 py-3 rounded-lg font-medium transition-all"
          >
            Request a Connector
          </a>
        </div>
      </section>
    </div>
  );
}
